import React, { useMemo } from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem } from '../../../../lib/api/settlementCompleteDateAdjustment';
import { PAY_METHOD_TYPE_TEXT } from '../../../../constants/service/app/orderConstants';
import { PRODUCT_MANAGEMENT_TYPE_TEXT } from '../../../../constants/service/app/productConstants';
import {
  SETTLEMENT_ITEM_PERIOD_TYPE_LABEL,
  SETTLEMENT_ITEM_STATUS_LABEL,
  SETTLEMENT_ITEM_TYPE_LABEL,
  SETTLEMENT_MANAGEMENT_LABEL,
} from '../../constants/labels';
import { formatAmount, formatDateTime } from './formatters';

/**
 * 변경 대상 정산 아이템 테이블의 컬럼 정의.
 *
 * 정산번호·브랜드·브랜드 ID 3개 좌측 고정 (기존 정산 리스트 컨벤션 유지).
 * 정산번호는 hasHistory=true 인 행만 클릭 가능한 링크로 렌더 — 클릭 시 이력 모달을 연다.
 *
 * 기준일 변경과의 차이: 변경 대상 필드가 `completeDate` 단일이므로 baseDate/dueDate
 * 두 컬럼 대신 "정산 완료일" 한 컬럼만 표시한다.
 */
export const useEligibleItemsColumns = (
  onSettlementItemClick: (settlementItemId: number) => void,
): ColumnsType<EligibleItem> =>
  useMemo(
    () => [
      {
        title: '정산번호',
        dataIndex: 'id',
        width: 100,
        fixed: 'left' as const,
        render: (v: number, r: EligibleItem) =>
          r.hasHistory ? (
            <a
              role="button"
              onClick={() => onSettlementItemClick(v)}
              style={{ color: '#6200ea', fontWeight: 600, cursor: 'pointer' }}
            >
              {v}
            </a>
          ) : (
            <span>{v}</span>
          ),
      },
      {
        title: '브랜드',
        dataIndex: 'brandName',
        width: 140,
        fixed: 'left' as const,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '브랜드 ID',
        dataIndex: 'storeId',
        width: 100,
        fixed: 'left' as const,
      },
      { title: '주문 번호', dataIndex: 'orderId', width: 140, render: (v?: string) => v ?? '-' },
      { title: '상품 주문 번호', dataIndex: 'orderItemId', width: 140, render: (v?: string) => v ?? '-' },
      {
        title: '정산 주기 타입',
        dataIndex: 'periodType',
        width: 120,
        render: (v: EligibleItem['periodType']) =>
          SETTLEMENT_ITEM_PERIOD_TYPE_LABEL[v] ?? v,
      },
      {
        title: '월간 정산 주기',
        dataIndex: 'monthlyExecutionPeriod',
        width: 110,
        render: (v?: number) => v ?? '-',
      },
      {
        title: '월간 정산일',
        dataIndex: 'monthlyExecutionDay',
        width: 100,
        render: (v?: number) => v ?? '-',
      },
      {
        title: '정산 완료일',
        dataIndex: 'completeDate',
        width: 120,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '상태',
        dataIndex: 'status',
        width: 90,
        render: (v: EligibleItem['status']) =>
          SETTLEMENT_ITEM_STATUS_LABEL[v] ?? v,
      },
      {
        title: '구분',
        dataIndex: 'type',
        width: 110,
        render: (v: EligibleItem['type']) => SETTLEMENT_ITEM_TYPE_LABEL[v] ?? v,
      },
      {
        title: '상품번호',
        dataIndex: 'productId',
        width: 100,
        render: (v?: number) => v ?? '-',
      },
      {
        title: 'PG사 정보',
        dataIndex: 'pgProviderKorName',
        width: 120,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '결제수단',
        dataIndex: 'paymentMethod',
        width: 140,
        render: (v?: number) =>
          typeof v === 'number' ? PAY_METHOD_TYPE_TEXT[v] ?? `코드 ${v}` : '-',
      },
      {
        title: '구매자명',
        dataIndex: 'userName',
        width: 140,
        render: (v?: string) => v ?? '-',
      },
      { title: '정산금액', dataIndex: 'amount', width: 110, render: formatAmount },
      { title: '판매금액', dataIndex: 'salesAmount', width: 110, render: formatAmount },
      { title: '총 쿠폰 사용금액', dataIndex: 'usedCouponAmount', width: 140, render: formatAmount },
      { title: '쿠폰 분담액(amondz)', dataIndex: 'amondzCouponAmount', width: 160, render: formatAmount },
      { title: '쿠폰 분담액(브랜드)', dataIndex: 'couponShareAmount', width: 160, render: formatAmount },
      { title: '포인트', dataIndex: 'usedPointAmount', width: 100, render: formatAmount },
      { title: '결제금액', dataIndex: 'paidAmount', width: 110, render: formatAmount },
      { title: '결제일시', dataIndex: 'paymentDate', width: 160, render: formatDateTime },
      { title: '구매 확정 일시', dataIndex: 'purchaseConfirmDate', width: 160, render: formatDateTime },
      { title: '수수료 합계', dataIndex: 'sumFee', width: 110, render: formatAmount },
      {
        title: '관리처',
        dataIndex: 'management',
        width: 100,
        render: (v?: number) =>
          typeof v === 'number' ? SETTLEMENT_MANAGEMENT_LABEL[v] ?? `코드 ${v}` : '-',
      },
      {
        title: '타입',
        dataIndex: 'productManagementType',
        width: 100,
        render: (v?: number) =>
          typeof v === 'number'
            ? PRODUCT_MANAGEMENT_TYPE_TEXT[v] ?? `코드 ${v}`
            : '-',
      },
      { title: 'PG 수수료', dataIndex: 'pgFee', width: 100, render: formatAmount },
      { title: 'AMONDZ 수수료', dataIndex: 'amondzFee', width: 120, render: formatAmount },
      { title: '마케팅 수수료', dataIndex: 'marketingFee', width: 120, render: formatAmount },
      { title: '풀필먼트 수수료', dataIndex: 'fulfillmentFee', width: 130, render: formatAmount },
      { title: 'PG 수수료 VAT', dataIndex: 'pgFeeVat', width: 130, render: formatAmount },
      { title: 'AMONDZ 수수료 VAT', dataIndex: 'amondzFeeVat', width: 150, render: formatAmount },
      { title: '마케팅 수수료 VAT', dataIndex: 'marketingFeeVat', width: 150, render: formatAmount },
      { title: '풀필먼트 수수료 VAT', dataIndex: 'fulfillmentFeeVat', width: 160, render: formatAmount },
      {
        title: '예금주',
        dataIndex: 'accountHolder',
        width: 120,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '계좌번호',
        dataIndex: 'accountNumber',
        width: 140,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '은행',
        dataIndex: 'bankName',
        width: 100,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '판매자 연락처',
        dataIndex: 'brandPhone',
        width: 140,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '관리자',
        dataIndex: 'adminId',
        width: 140,
        render: (_: unknown, r: EligibleItem) =>
          r.adminName ? `${r.adminName}(${r.adminId})` : '-',
      },
      {
        title: '메모',
        dataIndex: 'memo',
        width: 160,
        render: (v?: string) => v ?? '-',
      },
      {
        title: '주의',
        key: 'warning',
        width: 140,
        fixed: 'right' as const,
        render: (_: unknown, r: EligibleItem) =>
          r.isInActiveBatch ? <Tag color="orange">활성 배치 포함</Tag> : null,
      },
    ],
    [onSettlementItemClick],
  );
